/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import debug from 'debug';
import { some, none } from 'fp-ts/lib/Option';

import { loop, getApiUrl, loadTerms } from 'sdi/app';
import { DIV, SPAN, NodeOrOptional, BUTTON } from 'sdi/components/elements';
import header from 'sdi/components/header';
import footer from 'sdi/components/footer';
import tr from 'sdi/locale';

import map from './components/map';
import table from './components/table/feature-collection2';
import feature from './components/feature-view';
import legend, { switcher as legendSwitch } from './components/legend';
import home from './components/home';
import tracker from './components/geo-tracker';
import measure from './components/geo-measure';
import extract from './components/extract';
import print from './components/print';
import query from './components/query';
import modal from './components/modal';
import {
    loadAllBaseLayers,
    loadCategories,
    loadAlias,
    loadBaseLayerCategories,
    loadRoles,
    loadPointOfContactList,
} from './events/app';
import { getLayout, isTableSelected } from './queries/app';

import { viewEvents } from './events/map';
import { loadInitialRoute, navigateHome } from './events/route';
import { maintenance } from 'sdi/components/maintenance';
import { icon } from 'sdi/components/button';
import { clearServiceResponse, loadAllServices } from 'sdi/geocoder/events';

const tablePane = () =>
    DIV(
        {
            className: 'table-pane',
            onClick: () => clearServiceResponse(),
        },
        table(),
    );

const logger = debug('sdi:app');

const renderAppListingButton = () => {
    if (getLayout() !== 'map-navigator-fs') {
        return some(
            DIV(
                'app-listwrapper',
                BUTTON(
                    {
                        className: 'navigate app-listview',
                        onClick: () => navigateHome(),
                    },
                    icon('ellipsis-h'),
                    SPAN({ className: 'label' }, tr.view('mapList')),
                ),
            ),
        );
    }
    return none;
};

const wrappedMain = (name: string, ...elements: NodeOrOptional[]) =>
    DIV(
        'app-inner view-inner',
        modal(),
        header('view', { navigateHome, appElement: renderAppListingButton() }),
        maintenance('view'),
        DIV({ className: `main ${name}` }, ...elements),
        footer(),
    );

const renderMapFs = () =>
    isTableSelected().fold(
        wrappedMain(
            'map-fs',
            legendSwitch(),
            legend(),
            map(),
            // baseLayerSwitch()
        ),
        () =>
            wrappedMain(
                'map-and-table',
                legendSwitch(),
                legend(),
                DIV('vertical-split', map(), tablePane()),
            ),
    );

const renderMapAndInfo = () =>
    isTableSelected().fold(
        wrappedMain(
            'map-and-info',
            legendSwitch(),
            legend(),
            map(),
            // baseLayerSwitch()
        ),
        () =>
            wrappedMain(
                'map-and-table',
                legendSwitch(),
                legend(),
                DIV('vertical-split', map(), tablePane()),
            ),
    );

const renderMapAndFeature = () =>
    isTableSelected().fold(
        wrappedMain(
            'map-and-info',
            legendSwitch(),
            feature(),
            map(),
            // baseLayerSwitch()
        ),
        () =>
            wrappedMain(
                'map-and-table',
                legendSwitch(),
                feature(),
                DIV('vertical-split', map(), tablePane()),
            ),
    );

// const renderTableFs = () => wrappedMain('table-fs', table());

// const renderMapAndTable = () =>
//     wrappedMain(
//         'map-and-table',
//         legendSwitch(),
//         legend(),
//         DIV({ className: 'vertical-split' }, map(), table())
//     );

const renderHome = () => wrappedMain('map-navigator-fs', home());

// const renderMapAndTableAndFeature = () =>
//     wrappedMain(
//         'map-and-table',
//         legendSwitch(),
//         feature(),
//         DIV({ className: 'vertical-split' }, map(), table())
//     );

const renderMapAndTracker = () =>
    wrappedMain('map-and-tracker', tracker(), map());

const renderMapAndMeasure = () =>
    wrappedMain('map-and-measure', measure(), map());

const renderMapAndExtract = () =>
    wrappedMain(
        'map-and-extract',
        extract(),
        isTableSelected().fold(map(), () =>
            DIV('vertical-split', map(), tablePane()),
        ),
    );

const renderPrint = () => wrappedMain('print', print(), map());

const renderQuery = () => wrappedMain('query', query());

const renderQueryPrint = () => wrappedMain('query', query(true));

const renderMain = () => {
    const layout = getLayout();
    logger(`LAYOUT: ${layout}`);
    switch (layout) {
        case 'map-fs':
            return renderMapFs();
        case 'map-and-info':
            return renderMapAndInfo();
        case 'map-and-feature':
            return renderMapAndFeature();
        case 'map-navigator-fs':
            return renderHome();
        case 'map-and-tracker':
            return renderMapAndTracker();
        case 'map-and-measure':
            return renderMapAndMeasure();
        case 'map-and-extract':
            return renderMapAndExtract();
        case 'print':
            return renderPrint();
        case 'query':
            return renderQuery();
        case 'query-print':
            return renderQueryPrint();
    }
};

const effects = (initialRoute: string[]) => () => {
    loadInitialRoute(initialRoute);
    loadAllServices();
    viewEvents.updateMapView({ dirty: 'geo' });
    loadAllBaseLayers(getApiUrl(`wmslayers/`));
    loadBaseLayerCategories(getApiUrl(`wms_categories/`));
    loadCategories(getApiUrl(`categories`));
    loadRoles(getApiUrl(`roles_categories`));
    loadAlias(getApiUrl(`alias`));
    loadPointOfContactList(`md/poc`);
    // navigate();
    tr.init_edited();
    loadTerms();

    // activityLogger(langAction(getLang()));
    // activityLogger(visitAction());
};

const app = (initialRoute: string[]) =>
    loop('view', renderMain, effects(initialRoute));
export default app;

logger('loaded');
